import type { BannerDisplayLocation } from '~/constants/banners'

export const useBanner = (displayLocation: BannerDisplayLocation) => {
  const bannerApi = useApi(BannerApi)
  const bannersStore = useState<
    Record<number, GetBanners200ResponseBannersInner[] | undefined>
  >('banners', () => ({}))
  const bannerStatus = useLocalStorage<LocalStorageBannerStatus>(
    LOCAL_STORAGE_BANNER_STATUS,
    {},
  )
  const dayjs = useDayjs()

  const loadBanners = async (count: number = 1) => {
    if (bannersStore.value[displayLocation]) {
      return
    }
    try {
      const result = await bannerApi.getBanners({
        displayLocation,
      })

      const targetBanners = result.banners.slice(0, count)
      if (targetBanners.length === 0) {
        bannersStore.value[displayLocation] = []
        return
      }

      bannersStore.value[displayLocation] = targetBanners
    } catch (error) {
      console.error(error)
      bannersStore.value[displayLocation] = undefined
    }
  }

  const clearBanners = () => {
    bannersStore.value[displayLocation] = undefined
  }

  const closeBanner = (bannerId: number) => {
    bannerStatus.value = {
      ...bannerStatus.value,
      [bannerId]: dayjs().format('YYYY-MM-DD'),
    }
  }

  const targetBanners = computed(
    () => bannersStore.value[displayLocation] || [],
  )

  const banners = computed(() => {
    const today = dayjs().startOf('day')
    const displayBanners = targetBanners.value.filter((banner) => {
      const lastDisplayDate = bannerStatus.value[banner.bannerId]
      if (!lastDisplayDate) {
        return true
      }
      const lastDisplayDateDayjs = dayjs(lastDisplayDate)
      if (!lastDisplayDateDayjs.isValid()) {
        return true
      }

      switch (banner.displayInterval) {
        case BANNER_DISPLAY_INTERVAL_ONCE:
          return false
        case BANNER_DISPLAY_INTERVAL_DAILY:
          return today.diff(dayjs(lastDisplayDate).startOf('day'), 'day') >= 1
        case BANNER_DISPLAY_INTERVAL_EVERY_3_DAYS:
          return today.diff(dayjs(lastDisplayDate).startOf('day'), 'day') >= 3
        case BANNER_DISPLAY_INTERVAL_WEEKLY:
          return today.diff(dayjs(lastDisplayDate).startOf('day'), 'day') >= 7
        default:
          return false
      }
    })
    return displayBanners
  })

  const banner = computed(() => banners.value[0] || undefined)

  return {
    banner,
    banners,
    loadBanners,
    clearBanners,
    closeBanner,
  }
}
